import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { AccountsContext } from "context/AccountsContext";
import IgnitionReportsTable from "./ignitionTripReportTable";
import DateRangePicker from "layouts/DateTimerange/dateTimeRange";
import DhundhooLoader from "../Loader/Loader";

function IgnitionTripReport() {
  const [reportData, setReportData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vehicleId, setVehicleId] = useState(""); // ✅ Single vehicle only

  const { fetchThings, things } = useContext(AccountsContext);

  useEffect(() => {
    fetchThings();
  }, []);

  // ✅ Aaj (Today) ki date nikalne ka logic
  const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const todayStr = getTodayDateString();
  const defaultStart = `${todayStr}T00:00`;
  const defaultEnd = `${todayStr}T23:59`;

  // ✅ By default today's date is selected
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);

  // Snackbar handlers
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // Table columns
  const columns = [
    { Header: "Trip ID", accessor: "id", width: "10%" },
    { Header: "Start Time", accessor: "ignStartTimeDate", width: "20%" },
    { Header: "Start Location", accessor: "startLocation", width: "25%" },
    { Header: "End Time", accessor: "ignEndTimeDate", width: "20%" },
    { Header: "End Location", accessor: "endLocation", width: "25%" },
  ];

  // Fetch report
  const fetchReport = async () => {
    if (!vehicleId) {
      setErrorMessage("Please select a vehicle first.");
      openErrorSB();
      return;
    }

    if (!startTime || !endTime) {
      setErrorMessage("Please select a date range first.");
      openErrorSB();
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    setErrorMessage("");

    const formatDateYYYYMMDD = (isoString) => {
      const [d] = isoString.split("T");
      return d;
    };

    try {
      const data = {
        startDate: formatDateYYYYMMDD(startTime),
        endDate: formatDateYYYYMMDD(endTime),
      };

      const config = {
        method: "post",
        url: `https://v3.dhundhoo.com/reports/ignitionTripReport/${vehicleId}`,
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data,
      };

      const response = await axios.request(config);

      // 🔥 Object → Array conversion
      const formattedRows = Object.values(response.data || {}).map((log, index) => {
        const startLat = log.startLatitude;
        const startLng = log.startLongitude;
        const endLat = log.endLatitude;
        const endLng = log.endLongitude;

        return {
          id: index + 1,
          ignStartTimeDate: log.startTime,
          ignEndTimeDate: log.endTime,

          // 🔥 RAW VALUES (CSV ke liye)
          startLat,
          startLng,
          endLat,
          endLng,

          // UI ke liye (Map Links)
          startLocation: (
            <a
              href={`https://www.google.com/maps?q=${startLat},${startLng}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1a73e8", textDecoration: "underline", fontSize: "0.85rem", fontWeight: "bold" }}
            >
              View Start
            </a>
          ),

          endLocation: (
            <a
              href={`https://www.google.com/maps?q=${endLat},${endLng}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#e53935", textDecoration: "underline", fontSize: "0.85rem", fontWeight: "bold" }}
            >
              View End
            </a>
          ),
        };
      });

      setReportData(formattedRows);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading ignition trip report");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  // Export CSV
  const exportToCSV = () => {
    if (!reportData || reportData.length === 0) {
      setErrorMessage("No data to export");
      openErrorSB();
      return;
    }

     const formatFileDate = (dateTime) => {
  if (!dateTime) return "";

  const date = new Date(dateTime);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const formattedDate = `${formatFileDate(startTime)}_to_${formatFileDate(endTime)}`;

    const headers = [
      "Trip ID",
      "Start Time",
      "Start Latitude",
      "Start Longitude",
      "End Time",
      "End Latitude",
      "End Longitude",
    ];

    const rows = reportData.map((row) => [
      row.id || "",
      row.ignStartTimeDate || "",
      row.startLat || "",
      row.startLng || "",
      row.ignEndTimeDate || "",
      row.endLat || "",
      row.endLng || "",
    ]);

    const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `IgnitionTripReport_${formattedDate}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setVehicleId(""); // ✅ Reset single vehicle
    setReportData([]);
    setErrorMessage("");
    setStartTime(defaultStart); // ✅ Reset to today's date
    setEndTime(defaultEnd);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2}>
                    {/* Vehicle Select */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        options={things || []}
                        getOptionLabel={(option) => option.thingId || ""}
                        value={things?.find((t) => t.thingId === vehicleId) || null}
                        onChange={(event, newValue) => {
                          setVehicleId(newValue ? newValue.thingId : "");
                        }}
                        renderOption={(props, option) => (
                          <li {...props} style={{ display: "flex", justifyContent: "space-between", width: "100%", fontSize: "0.85rem" }}>
                            <strong>{option.nickName}</strong>
                            <span style={{ color: "#666", marginLeft: "10px" }}>{option.thingId}</span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label="Select Vehicle" fullWidth size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#1398DC", // ✅ border on click
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#1398DC", // ✅ label color
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ✅ Custom DateRangePicker */}
                    <Grid item xs={12} md={6}>
                      <div style={{ width: '46%' }}>
                        <DateRangePicker
                          startTime={startTime}
                          endTime={endTime}
                          onChange={({ startTime: s, endTime: e }) => {
                            setStartTime(s);
                            setEndTime(e);
                          }}
                          disabled={loading}
                        />
                      </div>
                    </Grid>

                    {/* Buttons */}
                    <Grid item xs={12} md={3}>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <MDButton fullWidth variant="gradient" color="info" size="small" onClick={fetchReport} disabled={loading}>
                            {loading ? <CircularProgress size={15} color="inherit" /> : "LOAD"}
                          </MDButton>
                        </Grid>
                        <Grid item xs={4}>
                          <MDButton fullWidth variant="outlined" color="error" size="small" onClick={clearFilters} disabled={loading}>
                            CLEAR
                          </MDButton>
                        </Grid>
                        <Grid item xs={4}>
                          <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading || reportData.length === 0} />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {/* Error Alert */}
              {errorMessage && (
                <MDBox px={3} pt={2}>
                  <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert>
                </MDBox>
              )}

              {/* Loader or Table */}
              <MDBox pt={3} display="flex" justifyContent="center">
                {loading ? (
                  <MDBox p={5}>
                    <DhundhooLoader />
                  </MDBox>
                ) : (
                  <IgnitionReportsTable
                    table={{ columns, rows: reportData }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Snackbars */}
      <MDSnackbar color="success" icon="check" title="Ignition Trip Report" content="Data Loaded Successfully" open={successSB} onClose={closeSuccessSB} close={closeSuccessSB} bgWhite />
      <MDSnackbar color="error" icon="warning" title="Ignition Trip Report" content={errorMessage || "Failed to load report"} open={errorSB} onClose={closeErrorSB} close={closeErrorSB} bgWhite />
    </DashboardLayout>
  );
}

export default IgnitionTripReport;